import { Nullable } from '@infotech-soe/merps/types/common';
import {
  ActionName,
  ActionProcessMode,
  IContextMenuItemRefreshRules,
  IDisplayModeParsed,
  ShowCategoryActionP,
  TApplicationName,
} from '@infotech-soe/metaschema';

import { TActionArgsValues } from '../../../infrastructure/providers/abstract/IApiProvider';
import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import CategoryViewModel from '../../category/view-model/CategoryViewModel';
import { ClientContext } from '../../context/IContextService';
import { DatasetGridRow } from '../../dataset/view-model/DatasetGridRow';
import { getAppDisplayModeName } from '../../form/builders/controls/helpers/getAppDisplayModeName';
import UniqueId from '../../uid/UniqueId';
import { UserInteractor, UserInteractorType } from '../UserInteractor';

import CategoryCommandImpl from './CategoryCommandImpl';
import { processShowCategoryInParams } from './processShowCategoryInParams';

class ShowCategoryCommandImpl extends CategoryCommandImpl<TActionArgsValues> {
  constructor(
    private application: Nullable<TApplicationName>,
    private action: ShowCategoryActionP,
    private category: CategoryViewModel,
    private actionName: ActionName,
    private datasetId: UniqueId,
    private refreshRules?: IContextMenuItemRefreshRules
  ) {
    super();
  }

  public async execute(): Promise<void | TActionArgsValues> {
    // not implemented
  }

  public async getInitialArgs(
    context: ClientContext,
    currentRecord: DatasetGridRow
  ): Promise<TActionArgsValues> {
    if (!this.action.InParams) {
      return {};
    }

    const datasetName = this.category.datasets[this.datasetId].name;

    let displayModeSchema: IDisplayModeParsed | undefined;

    if (context.has('DisplayMode')) {
      try {
        displayModeSchema = await new CachedSchemaRepository().getDisplayMode(
          context.get('MasterCategory') || context.get('Category'),
          context.get('DisplayMode')
        );
      } catch {
        displayModeSchema = undefined;
      }
    }

    return {
      inParams: await processShowCategoryInParams(
        context.toServerContext(),
        this.action.InParams,
        displayModeSchema?.datasets[datasetName]?.source,
        currentRecord,
        undefined
      ),
    };
  }

  public getUserInteractor(): UserInteractor {
    return {
      type: UserInteractorType.SHOW_CATEGORY,
      category: this.action.Category,
      displayMode: getAppDisplayModeName(
        this.application,
        this.action.DisplayMode
      ),
      nextAction: this.action.NextAction,
      action: this.actionName,
      dataset: this.datasetId,
      multiSelect: this.action.MultiSelect || false,
      hideTree: this.action.HideTree || false,
      fullHierarchy: this.action.FullHierarchy || false,
      hideCheckbox: this.action.HideCheckbox || false,
      refreshRules: this.refreshRules,
    };
  }

  public getName(): ActionName {
    return this.actionName;
  }

  public getProcessMode(): ActionProcessMode {
    return this.action.ProcessMode || 'Current';
  }

  public getRefreshRules(): IContextMenuItemRefreshRules | undefined {
    return this.refreshRules;
  }
}

export default ShowCategoryCommandImpl;
